import type { DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import { resolveTanSuatNumber } from '@/components/common/ActionPlan/CongViecDuAn/CommonComponent/chiaChiTieuModal.helpers';
import { itemDayRange, itemKetThucDayKey, parseYMD, toDayKey } from '@/components/common/ActionPlan/utils/baoCaoNhanhVtnTree';
import { CONSTANTS } from '@/utils/constants';
import { flattenLeafTasks } from './tree';

// #region task

/** Bộ phận phụ trách của việc. */
export const resolveTaskDeptId = (task: DuAnCongViecTree): string =>
  String(task.congViec?.boPhanPhuTrachID || '').trim();

// #endregion task

// #region taskStatus

const { NGAY: TAN_SUAT_NGAY, BAO_CAO_CUOI_KY: TAN_SUAT_CUOI_KY } = CONSTANTS.ACTION_PLAN.TABLE.TANSUAT;

function endKeyOf(row: NonNullable<DuAnCongViecTree['listTanSuat']>[number]): string | null {
  return itemKetThucDayKey(row) || itemDayRange(row)?.end || null;
}

/** Khóa ngày cuối kỳ BC đang áp dụng. */
function getKyBaoCaoEndKey(item: DuAnCongViecTree, refDate = new Date()): string | null {
  const todayKey = toDayKey(refDate);
  const cv = item.congViec;
  const code = resolveTanSuatNumber(item);
  const list = item.listTanSuat ?? [];

  if (code === TAN_SUAT_NGAY) return todayKey;
  if (code === TAN_SUAT_CUOI_KY || list.length === 0) {
    return cv?.ngayKetThuc ? toDayKey(cv.ngayKetThuc) : null;
  }

  const inPeriod = list.find((row) => {
    const r = itemDayRange(row);
    return !!(r && todayKey && todayKey >= r.start && todayKey <= r.end);
  });
  if (inPeriod) return endKeyOf(inPeriod);

  const keys = list.map(endKeyOf).filter((k): k is string => !!k).sort();
  const next = keys.find((k) => !todayKey || k >= todayKey);
  return next ?? keys[keys.length - 1] ?? (cv?.ngayKetThuc ? toDayKey(cv.ngayKetThuc) : null);
}

/** Số ngày đến kỳ BC. Âm = quá hạn. null = không có ngày. */
export function getDaysUntilDeadline(item: DuAnCongViecTree, refDate?: Date): number | null {
  const endKey = getKyBaoCaoEndKey(item, refDate ?? new Date());
  const todayKey = toDayKey(refDate ?? new Date());
  if (!endKey || !todayKey) return null;
  const diff = parseYMD(endKey).getTime() - parseYMD(todayKey).getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

/** trangThai 4 = Trễ tiến độ (DuAnEnum) */
export function isOverdueTask(item: DuAnCongViecTree): boolean {
  if (isCompletedTask(item)) return false;
  if (item.trongKy?.trangThai === 4) return true;
  const days = getDaysUntilDeadline(item);
  return days !== null && days < 0;
}

export function isCompletedTask(item: DuAnCongViecTree): boolean {
  return item.trongKy?.trangThai === 3 || (item.trongKy?.hoanThanh ?? 0) >= 100;
}

/**
 * 5 trạng thái hiển thị theo kỳ báo cáo (TanSuat).
 * completed → overdue → today → upcoming → inprogress (thứ tự ưu tiên giảm dần)
 */
export type TanSuatStateGroup =
  | 'overdue'
  | 'today'
  | 'upcoming'
  | 'inprogress'
  | 'completed';

export function getTanSuatState(
  item: DuAnCongViecTree,
  todayTaskIdSet?: Set<string>,
): TanSuatStateGroup {
  if (isCompletedTask(item)) return 'completed';
  if (isOverdueTask(item)) return 'overdue';

  const id = item.congViec?.id;
  const hasTodayReport = (item.listBaoCaoTrongNgay?.length ?? 0) > 0;
  const inTodayList = !!(id && todayTaskIdSet?.has(id));
  const days = getDaysUntilDeadline(item);

  if (hasTodayReport || inTodayList || days === 0) return 'today';
  if (days !== null && days >= 1 && days <= 3) return 'upcoming';

  return 'inprogress';
}

const TAN_SUAT_STATE_PRIORITY: TanSuatStateGroup[] = [
  'overdue',
  'today',
  'upcoming',
  'inprogress',
];

/** Trạng thái khẩn nhất trong danh sách việc (ưu tiên: quá hạn → hôm nay → sắp đến hạn → đang làm). */
export function getWorstTanSuatState(
  items: DuAnCongViecTree[],
  todayTaskIdSet?: Set<string>,
): TanSuatStateGroup {
  const incomplete = items.filter((item) => !isCompletedTask(item));
  if (incomplete.length === 0) return 'completed';

  for (const state of TAN_SUAT_STATE_PRIORITY) {
    if (incomplete.some((item) => getTanSuatState(item, todayTaskIdSet) === state)) {
      return state;
    }
  }
  return 'inprogress';
}

/** Việc lá chưa hoàn thành dưới root AP (dùng cho section "Công việc đã giao"). */
export function getManagingActiveLeaves(node: DuAnCongViecTree): DuAnCongViecTree[] {
  return flattenLeafTasks([node]).filter((item) => !isCompletedTask(item));
}

export function isManagingItemFullyComplete(node: DuAnCongViecTree): boolean {
  const leaves = flattenLeafTasks([node]);
  return leaves.length > 0 && leaves.every(isCompletedTask);
}

/** Phân nhóm root AP theo trạng thái khẩn nhất của việc con chưa xong. */
export function getManagingItemTanSuatState(
  node: DuAnCongViecTree,
  todayTaskIdSet?: Set<string>,
): TanSuatStateGroup {
  if (isManagingItemFullyComplete(node)) return 'completed';
  return getWorstTanSuatState(getManagingActiveLeaves(node), todayTaskIdSet);
}

/** Node dùng hiển thị nhãn hạn — việc con quá hạn nhất (nếu còn việc chưa xong). */
export function getManagingItemDeadlineNode(node: DuAnCongViecTree): DuAnCongViecTree {
  const activeLeaves = getManagingActiveLeaves(node);
  if (activeLeaves.length === 0) return node;

  return activeLeaves.reduce((worst, current) => {
    const worstDays = getDaysUntilDeadline(worst);
    const currentDays = getDaysUntilDeadline(current);
    if (worstDays === null) return current;
    if (currentDays === null) return worst;
    return currentDays < worstDays ? current : worst;
  });
}

/** Nhóm leaf tasks theo 5 trạng thái (gồm completed). */
export function groupTasksByTanSuatState(
  items: DuAnCongViecTree[],
  todayTaskIdSet?: Set<string>,
): Record<TanSuatStateGroup, DuAnCongViecTree[]> {
  const groups: Record<TanSuatStateGroup, DuAnCongViecTree[]> = {
    overdue: [],
    today: [],
    upcoming: [],
    inprogress: [],
    completed: [],
  };
  for (const item of items) {
    groups[getTanSuatState(item, todayTaskIdSet)].push(item);
  }
  return groups;
}

export function groupTasksByMtctid(
  items: DuAnCongViecTree[],
): Map<string, DuAnCongViecTree[]> {
  const map = new Map<string, DuAnCongViecTree[]>();
  for (const item of items) {
    const mtctid = item.congViec?.mtctid || '_none';
    const list = map.get(mtctid) ?? [];
    list.push(item);
    map.set(mtctid, list);
  }
  return map;
}

/** ID nhân viên dạng chuẩn `<id>_nv` (API trả lẫn có / không hậu tố). */
export const normalizeEmployeeId = (raw?: string): string => {
  const id = String(raw || '').trim();
  if (!id) return '';
  return id.endsWith('_nv') ? id : `${id}_nv`;
};

export const isSameEmployeeId = (left?: string, right?: string): boolean => {
  const a = normalizeEmployeeId(left);
  const b = normalizeEmployeeId(right);
  return !!a && a === b;
};

/** ID người phụ trách chính (phần đầu nếu multi). */
export function resolveAssigneeId(item: DuAnCongViecTree): string {
  return String(item.congViec?.nguoiPhuTrachID || '').split(',')[0].trim();
}

// #endregion taskStatus
